import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Injectable,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import {
  MatCalendarCellClassFunction,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { TranslatorService } from '@sc/data-access-translation';
import { dayjs } from '@sc/utils';
import { PeriodPickerHeaderComponent } from './period-picker-header';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '../icon';
import { MatInputModule } from '@angular/material/input';
import { NgStyle, NgIf, NgClass } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';

@Injectable({
  providedIn: 'root',
})
@Component({
  selector: 'sc-period-picker',
  styleUrls: ['./period-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <mat-form-field [ngStyle]="{ width: width ? width + 'px' : null }">
      <mat-label *ngIf="label">{{ label }}</mat-label>
      <input
        matInput
        #input
        [value]="displayedValue"
        autocomplete="off"
        (click)="displayChoices = true"
      />
    </mat-form-field>
    <div class="choice" *ngIf="displayChoices">
      <div
        class="row"
        id="todayInterventionDateSelection"
        (click)="choseOption(1)"
      >
        <sc-icon
          [class.active]="chosenPeriodOptionIndex === 1"
          [name]="'calendar_hour'"
        ></sc-icon>
        <span [ngClass]="{ chosenPeriod: chosenPeriodOptionIndex === 1 }">{{
          'PeriodPicker.todayLabel' | translate
        }}</span>
      </div>
      <div
        class="row"
        id="asapInterventionDateSelection"
        (click)="choseOption(2)"
      >
        <sc-icon
          [class.active]="chosenPeriodOptionIndex === 2"
          [name]="'calendar_hour'"
        ></sc-icon>
        <span [ngClass]="{ chosenPeriod: chosenPeriodOptionIndex === 2 }">{{
          'PeriodPicker.asapLabel' | translate
        }}</span>
      </div>
      <div
        class="row"
        id="nextWeekInterventionDateSelection"
        (click)="choseOption(3)"
      >
        <sc-icon
          [class.active]="chosenPeriodOptionIndex === 3"
          [name]="'calendar_hour'"
        ></sc-icon>
        <span [ngClass]="{ chosenPeriod: chosenPeriodOptionIndex === 3 }">{{
          'PeriodPicker.nextWeekLabel' | translate
        }}</span>
      </div>
      <div
        class="row"
        id="incomingMonthInterventionDateSelection"
        (click)="choseOption(4)"
      >
        <sc-icon
          [class.active]="chosenPeriodOptionIndex === 4"
          [name]="'calendar_hour'"
        ></sc-icon>
        <span [ngClass]="{ chosenPeriod: chosenPeriodOptionIndex === 4 }"
          >{{ 'PeriodPicker.incomingMonthLabel' | translate }}
        </span>
      </div>
      <div
        class="row"
        id="customInterventionDateSelection"
        (click)="choseOption(5)"
      >
        <sc-icon
          [class.active]="chosenPeriodOptionIndex === 5"
          [name]="'calendar_hour'"
        ></sc-icon>
        <span [ngClass]="{ chosenPeriod: chosenPeriodOptionIndex === 5 }">{{
          'PeriodPicker.customisedLabel' | translate
        }}</span>
      </div>
    </div>

    <div *ngIf="form" [formGroup]="form">
      <mat-date-range-picker
        #picker
        touchUi
        [dateClass]="dateClass"
        [calendarHeaderComponent]="periodPickerHeader"
        (closed)="onMatDateRangePickerClose()"
        [opened]="isMatDateRangePickerOpened"
      ></mat-date-range-picker>
      <mat-date-range-input
        [rangePicker]="picker"
        [min]="minPeriodPickerDate"
        [max]="maxPeriodPickerDate"
      >
        <input
          #startDate
          matStartDate
          formControlName="startDate"
          (dateInput)="onStartDateChange(startDate?.value)"
        />
        <input
          #endDate
          matEndDate
          formControlName="endDate"
          (dateInput)="onEndDateChange(startDate?.value, endDate?.value)"
        />
      </mat-date-range-input>
    </div>
  `,
  imports: [
    MatFormFieldModule,
    NgStyle,
    NgIf,
    MatInputModule,
    IconComponent,
    NgClass,
    ReactiveFormsModule,
    MatDatepickerModule,
    TranslateModule,
  ],
})
export class PeriodPickerComponent implements OnInit {
  @Input() width: number;
  @Input() label: string;
  @Input() set _initValue(value: { startDate: Date; endDate: Date }) {
    if (value) {
      this.initValue = value;
      this.initDisplayedValue();
    }
  }
  @Output() validateDatesEvent = new EventEmitter<{
    startDate: Date;
    endDate: Date;
  }>();

  startDate: Date;
  endDate: Date;
  initValue: { startDate: Date; endDate: Date };

  value: { startDate: Date; endDate: Date };
  displayedValue: string;

  periodPickerHeader = PeriodPickerHeaderComponent;
  minPeriodPickerDate = new Date();
  maxPeriodPickerDate = this.getMaxPeriodPickerDate();
  displayChoices = false;
  chosenPeriodOptionIndex = null;
  isMatDateRangePickerOpened = false;

  form: FormGroup;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly translator: TranslatorService,
    private readonly translateService: TranslatorService,
    private readonly elemRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.translateService.getCurrentLang().subscribe((newFormattedLang) => {
      this.dateAdapter.setLocale(newFormattedLang);
    });
    this.initDisplayedValue();
  }

  initForm() {
    this.form = this.formBuilder.group({
      startDate: [this.value?.startDate],
      endDate: [this.value?.endDate],
    });
  }

  initDisplayedValue() {
    this.value = this.initValue;
    if (this.initValue) {
      this.form
        ?.get('startDate')
        ?.setValue(dayjs(this.value.startDate).utc().startOf('day'));
      this.form
        ?.get('startDate')
        ?.setValue(dayjs(this.value.endDate).utc().endOf('day'));
      this.displayedValue = this.parseDisplayedDate(
        this.value.startDate,
        this.value.endDate
      );
      this.choseOptionAutomatically();
      this.changeDetectorRef.detectChanges();
      this.chosePeriodDates(this.value.startDate, this.value.endDate);
      this.initValue = null;
    }
  }

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    if (view === 'month') {
      if (this.equalDates(cellDate, this.startDate)) {
        return 'start-date-period-picker';
      }

      if (this.equalDates(cellDate, this.endDate)) {
        return 'end-date-period-picker';
      }

      if (this.inRange(cellDate, this.startDate, this.endDate)) {
        return 'in-range-period-picker';
      }

      if (this.isToday(cellDate)) {
        return 'current-day-period-picker';
      }
    }

    return '';
  };

  isToday(date: Date): boolean {
    const _date = new Date(date);
    const today = new Date();
    return this.equalDates(_date, today);
  }

  private inRange(cellDate: Date, startDate: Date, endDate: Date): boolean {
    cellDate = new Date(cellDate);
    startDate = new Date(startDate);
    endDate = new Date(endDate);
    return cellDate > startDate && cellDate < endDate;
  }

  private equalDates(firstDate: Date, secondDate: Date): boolean {
    firstDate = new Date(firstDate);
    secondDate = new Date(secondDate);
    if (!firstDate || !secondDate) {
      return false;
    }

    return (
      firstDate.getFullYear() === secondDate.getFullYear() &&
      firstDate.getMonth() === secondDate.getMonth() &&
      firstDate.getDate() === secondDate.getDate()
    );
  }

  onStartDateChange(startDate: string) {
    this.startDate = new Date(this.parseStrToDate(startDate));
    this.endDate = null;

    this.displayedValue = this.getDisplayedDate(startDate);
    this.changeDetectorRef.detectChanges();

    this.value = { startDate: this.startDate, endDate: this.endDate };
  }

  onEndDateChange(startDate: string, endDate: string) {
    this.startDate = new Date(this.parseStrToDate(startDate));
    this.endDate = new Date(this.parseStrToDate(endDate));

    this.displayedValue = this.getDisplayedDate(
      startDate.slice(0, -2),
      endDate.slice(0, -2)
    );
    this.changeDetectorRef.detectChanges();

    this.value = { startDate: this.startDate, endDate: this.endDate };

    this.choseOptionAutomatically();
  }

  choseOptionAutomatically(): void {
    if (!this.equalDates(this.value?.startDate, new Date())) {
      this.chosenPeriodOptionIndex = 5;
      return;
    }

    const diffInDays = this.differenceBetweenDatesInDays(
      new Date(),
      this.value.endDate
    );
    let period = '';
    switch (diffInDays) {
      case 1:
        this.chosenPeriodOptionIndex = 1;
        period = this.translator.getInstantTranslation(
          'PeriodPicker.todayLabel'
        );
        this.displayLabelPeriod(period);
        break;
      case 15:
        this.chosenPeriodOptionIndex = 2;
        period = this.translator.getInstantTranslation(
          'PeriodPicker.asapLabel'
        );
        this.displayLabelPeriod(period);
        break;
      case 7:
        this.chosenPeriodOptionIndex = 3;
        period = this.translator.getInstantTranslation(
          'PeriodPicker.nextWeekLabel'
        );
        this.displayLabelPeriod(period);
        break;
      case 30:
        this.chosenPeriodOptionIndex = 4;
        period = this.translator.getInstantTranslation(
          'PeriodPicker.incomingMonthLabel'
        );
        this.displayLabelPeriod(period);
        break;
      default:
        this.chosenPeriodOptionIndex = 5;
    }
  }

  private displayLabelPeriod(label: string): void {
    this.displayedValue = label;
    this.changeDetectorRef.detectChanges();
  }

  private parseStrToDate(dateStr: string): Date {
    if (dateStr.split('/').length !== 3) {
      return null;
    }
    return new Date(
      +dateStr.split('/')[2],
      +dateStr.split('/')[1] - 1,
      +dateStr.split('/')[0]
    );
  }

  private parseDateToStr(date: Date): string {
    if (!date) {
      return '';
    }
    return (
      date.getDate() +
      '/' +
      (date.getMonth() + 1) +
      '/' +
      date.getFullYear().toString().substring(2)
    );
  }

  private parseDisplayedDate(startDate: Date, endDate: Date): string {
    const startDateStr = this.parseDateToStr(startDate);
    const endDateStr = this.parseDateToStr(endDate);
    return this.getDisplayedDate(startDateStr, endDateStr);
  }

  private getDisplayedDate(startDateStr: string, endDateStr?: string): string {
    if (!startDateStr) {
      return null;
    }

    if (startDateStr === endDateStr) {
      return startDateStr;
    }

    return endDateStr
      ? this.translateService.getInstantTranslation(
          'PeriodPicker.rangeDateStartText'
        ) +
          ' ' +
          startDateStr +
          ' ' +
          this.translateService.getInstantTranslation(
            'PeriodPicker.rangeDateSeparatorText'
          ) +
          ' ' +
          endDateStr
      : this.translateService.getInstantTranslation(
          'PeriodPicker.rangeDateStartText'
        ) +
          ' ' +
          startDateStr;
  }

  choseOption(optionIndex: number): void {
    this.chosenPeriodOptionIndex = optionIndex;
    const currentDate = new Date();

    if (optionIndex === 1) {
      this.chosePeriodDates(currentDate, currentDate);
    }

    if (optionIndex === 2) {
      const endDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 15
      );
      this.chosePeriodDates(currentDate, endDate);
    }

    if (optionIndex === 3) {
      const endDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 7
      );
      this.chosePeriodDates(currentDate, endDate);
    }

    if (optionIndex === 4) {
      const endDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate() + 30
      );
      this.chosePeriodDates(currentDate, endDate);
    }

    if (optionIndex === 5) {
      this.displayChoices = false;
      this.isMatDateRangePickerOpened = true;
    }
  }

  chosePeriodDates(startDate: Date, endDate: Date): void {
    this.displayChoices = false;

    const internalStartDate = dayjs(startDate).utc().startOf('day');
    const internalEndDate = dayjs(endDate).utc().endOf('day');

    this.form?.get('startDate')?.setValue(internalStartDate);
    this.form?.get('startDate')?.setValue(internalEndDate);

    this.value = {
      startDate: internalStartDate.toDate(),
      endDate: internalEndDate.toDate(),
    };
    this.displayedValue = this.parseDisplayedDate(
      internalStartDate.toDate(),
      internalEndDate.toDate()
    );
    this.choseOptionAutomatically();
    this.changeDetectorRef.detectChanges();
    this.validateDatesEvent.emit(this.value);
  }

  private differenceBetweenDatesInDays(date1: Date, date2: Date): number {
    const diff = Math.abs(date1.getTime() - date2.getTime());
    return Math.ceil(diff / (1000 * 3600 * 24));
  }

  private getMaxPeriodPickerDate() {
    const date = new Date(
      this.minPeriodPickerDate.getFullYear(),
      this.minPeriodPickerDate.getMonth() + 2,
      -1
    );
    date.setDate(date.getDate() + 1);
    return date;
  }

  onMatDateRangePickerClose() {
    this.isMatDateRangePickerOpened = false;
    this.choseOptionAutomatically();
    this.validateDatesEvent.emit(this.value);
  }

  @HostListener('document:click', ['$event.target'])
  public onDocumentClick(targetElement) {
    const clickedInside = this.elemRef.nativeElement.contains(targetElement);
    if (!clickedInside && this.displayChoices) {
      this.displayChoices = false;
    }
  }
}
